import type { InteractionRegistry, RegistryPolicy } from '../registry/types';
import type { NormalizedInteraction } from '../types/normalized';
import { extractInteractionMetadataFromElement } from './dom-metadata';
import {
  isContainerAbsorbedInteraction,
  resolveHenIdFromTarget,
} from '../routing/resolve-component-id';
import {
  routeInteraction,
  type UnknownComponentHandler,
} from '../routing/registry-router';
import { shouldDispatchInteractionForDomEvent } from './interaction-dom-events';

/**
 * A configured click owns this control. The host listener skips the
 * marked element and any control inside it, so it does not beam a
 * second ElementClicked.
 */
export const CONFIGURED_ELEMENT_CLICK_ATTR =
  'data-edu-configured-element-click';

export function configuredClickOwns(element: Element): boolean {
  // Keep this equal to CONFIGURED_ELEMENT_CLICK_ATTR, and keep it inside
  // the function. The page rebuilds the function from toString(), so a
  // free reference would not resolve there.
  const attr = 'data-edu-configured-element-click';
  let node: Element | null = element;
  while (node) {
    if (node.hasAttribute(attr)) return true;
    node = node.parentElement;
  }
  return false;
}

export const DELEGATED_DOM_EVENT_TYPES = [
  'click',
  'change',
  'focusin',
  'focusout',
] as const;

export type DelegatedDomEventType = (typeof DELEGATED_DOM_EVENT_TYPES)[number];

export interface DelegatedInteractionsOptions {
  readonly root: Document | Element;
  readonly interactionRegistry: InteractionRegistry;
  readonly registryPolicy: RegistryPolicy;
  readonly onUnknownComponentId?: (componentId: string) => void;
  readonly onInteraction: (interaction: NormalizedInteraction) => void;
}

export function subscribeDelegatedInteractions(
  options: DelegatedInteractionsOptions,
): () => void {
  const handler: UnknownComponentHandler = {
    policy: options.registryPolicy,
    onUnknownComponentId: options.onUnknownComponentId,
  };

  const listener = (event: Event): void => {
    const domEventType = event.type as DelegatedDomEventType;
    if (!DELEGATED_DOM_EVENT_TYPES.includes(domEventType)) {
      return;
    }

    const resolution = resolveHenIdFromTarget(event.target);
    if (
      !resolution ||
      !shouldDispatchInteractionForDomEvent(
        domEventType,
        resolution.componentId,
      ) ||
      (domEventType === 'click' && configuredClickOwns(resolution.host)) ||
      (domEventType === 'click' &&
        isContainerAbsorbedInteraction(event.target, resolution.componentId))
    ) {
      return;
    }

    const metadata = extractInteractionMetadataFromElement(
      resolution.host,
      event.target,
    );

    const interaction = routeInteraction(
      resolution.componentId,
      options.interactionRegistry,
      handler,
      { metadata },
    );
    if (interaction) {
      options.onInteraction(interaction);
    }
  };

  const { root } = options;
  for (const type of DELEGATED_DOM_EVENT_TYPES) {
    root.addEventListener(type, listener, true);
  }

  return () => {
    for (const type of DELEGATED_DOM_EVENT_TYPES) {
      root.removeEventListener(type, listener, true);
    }
  };
}
